import type { Ancestor } from "@/entities/ancestor";
import type { AssetCreatePayload } from "@/entities/asset";
import type { Coordinate } from "@/types";

import { useTranslation } from "react-i18next";

import { useCreate } from "@/hooks/queries";
import { useNavigateToRessource } from "@/hooks/useNavigateToRessource";
import { getAssetConditionRatingFieldValue } from "@/modules/asset/asset-condition-rating-field";
import { getAssetRiskFieldValue } from "@/modules/asset/asset-risk-field";
import { getAssetSelectOptionValue } from "@/modules/assets/assets-select-parent";
import { getLocationSelectOptionValue } from "@/modules/location/location-select-parent";

import AssetForm from "./AssetForm";

const invalidateKeys = [
  ["locations", "coordinates"],
  ["assets", "coordinates"],
  ["tree", "locations"],
];

type AssetCreateFormProps = {
  ancestors?: Ancestor[];
  defaultCoordinate?: Coordinate;
};

const Create = ({
  ancestors = [],
  defaultCoordinate,
}: AssetCreateFormProps) => {
  const { t } = useTranslation();

  const mutation = useCreate("assets", { invalidateKeys });

  const onNavigateToAsset = useNavigateToRessource<AssetCreatePayload>(
    "asset",
    ({ data }) => data.data.id
  );

  const initialValues = {
    location: getLocationSelectOptionValue(ancestors),
    asset: getAssetSelectOptionValue(ancestors),
    coordinate: defaultCoordinate,
    conditionRating: getAssetConditionRatingFieldValue(),
    risk: getAssetRiskFieldValue(),
  };

  return (
    <AssetForm
      mutation={mutation}
      saveButtonLabel={t("global:add")}
      initialValues={initialValues}
      onMutate={onNavigateToAsset}
    />
  );
};

export default Create;
